import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import ImageRequest from '@Containers/ImageRequest';
import * as FormatUtils from '@Services/FormatUtils';

import './StructurePreview.scss';

function StructurePreview({
  title = '',
  structureId = '',
  mixtureName = null,
  structureName = null,
  previewPathKey,
  previewPath,
  zoom = 0,
  markushPreview,
  chemicalNames,
}) {
  const customAttrs = useMemo(() => (title ? { title } : {}), [title]);

  const getNoImageComponent = () => (
    <div className="rx-picture no-image" {...customAttrs}>
      <div className="no-image-content">
        <div>No Structure Preview</div>
        {structureId && <div>RN: {structureId}</div>}
        {structureName && <div>{structureName}</div>}
      </div>
    </div>
  );

  const getMixtureNameComponent = () => (
    <div className="mixture-name-wrapper">
      <div className="mixture-name">{(mixtureName || []).join('; ')}</div>
    </div>
  );

  const getRegularPreviewComponent = () => {
    const altText = title ? <span className="screen-reader-text">{title}</span> : null;
    const previewUrl = previewPath?.[previewPathKey]?.url;
    const url = previewUrl && FormatUtils.formatPath(previewUrl);
    const image =
      previewPathKey && previewPath?.[previewPathKey]
        ? {
            ...previewPath[previewPathKey],
            url,
          }
        : FormatUtils.getImageFromPathForZoom(previewPath, zoom, true) || {};

    const imageGenerationRequestsLimit = Auth.isFeatureEnabled(FEATURES.image_generation_requests_limit);
    return image.url ? (
      <div className="structure-preview restrict-select" {...customAttrs}>
        {imageGenerationRequestsLimit ? (
          <ImageRequest
            url={image.url}
            limit={parseInt(imageGenerationRequestsLimit, 10)}
            render={({ src }) => (
              <img
                src={src}
                alt={`Chemical structure of ${chemicalNames?.[0] || customAttrs.title || image.label}`}
                loading="lazy"
                draggable="false"
                onDragStart={(e) => e.preventDefault()}
              />
            )}
          />
        ) : (
          <img
            src={image.url}
            alt={`Chemical structure of ${chemicalNames?.[0] || customAttrs.title || image.label}`}
            draggable="false"
            onDragStart={(e) => e.preventDefault()}
          />
        )}
      </div>
    ) : (
      <div className="structure-preview" {...customAttrs}>
        <InnerHTML tag="div" className="u-els-color-n6 u-els-font-family-bold no-image-structure" title={image.label} html={image.label} />
        {altText}
      </div>
    );
  };

  const getMarkushPreviewComponent = () => {
    const path = FormatUtils.formatPath(markushPreview.url);
    return <img src={path} alt={structureId || ''} draggable="false" onDragStart={(e) => e.preventDefault()} />;
  };

  if (markushPreview) return getMarkushPreviewComponent();
  if (!previewPath) return getNoImageComponent();
  if (mixtureName) return getMixtureNameComponent();
  return getRegularPreviewComponent();
}

StructurePreview.propTypes = {
  title: PropTypes.string,
  structureId: PropTypes.string,
  mixtureName: PropTypes.array,
  structureName: PropTypes.string,
  previewPathKey: PropTypes.string,
  previewPath: PropTypes.object,
  zoom: PropTypes.number,
  markushPreview: PropTypes.object,
  chemicalNames: PropTypes.array,
};

export default StructurePreview;
